import type { WorkFilters } from "@/types/work";
import { TYPE_LABELS } from "@/lib/works";

interface FilterOptions {
  years: string[];
  regions: string[];
  genres: string[];
}

interface FilterPanelProps {
  filters: WorkFilters;
  options: FilterOptions;
  onToggle: (group: keyof WorkFilters, value: string) => void;
  onReset: () => void;
}

function FilterGroup({
  title,
  group,
  values,
  selected,
  onToggle,
  labelMap,
}: {
  title: string;
  group: keyof WorkFilters;
  values: string[];
  selected: string[];
  onToggle: (group: keyof WorkFilters, value: string) => void;
  labelMap?: Record<string, string>;
}) {
  return (
    <div>
      <h3 className="font-heading text-sm tracking-[0.16em] text-foreground-800">{title}</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {values.map((value) => {
          const isActive = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => onToggle(group, value)}
              aria-pressed={isActive}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap border ${
                isActive
                  ? "bg-primary-500 border-primary-500 text-foreground-950"
                  : "border-background-200/80 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-950"
              }`}
            >
              {labelMap?.[value] ?? value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({ filters, options, onToggle, onReset }: FilterPanelProps) {
  const activeCount =
    filters.types.length + filters.years.length + filters.regions.length + filters.genres.length;

  return (
    <div className="flex flex-col gap-7">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-400" />
          <span className="font-heading text-base text-foreground-950">筛选条件</span>
          {activeCount > 0 && (
            <span className="px-2 py-[2px] rounded-full bg-primary-500/20 text-primary-300 text-[11px]">{activeCount}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
        >
          重置
        </button>
      </div>

      <FilterGroup
        title="作品类型"
        group="types"
        values={["movie", "tv"]}
        selected={filters.types}
        onToggle={onToggle}
        labelMap={TYPE_LABELS}
      />
      <div className="h-px bg-background-200/70" />
      <FilterGroup title="上映年份" group="years" values={options.years} selected={filters.years} onToggle={onToggle} />
      <div className="h-px bg-background-200/70" />
      <FilterGroup title="地区" group="regions" values={options.regions} selected={filters.regions} onToggle={onToggle} />
      <div className="h-px bg-background-200/70" />
      <FilterGroup title="剧情类型" group="genres" values={options.genres} selected={filters.genres} onToggle={onToggle} />

      <div className="mt-1 rounded-md bg-secondary-900/50 border border-secondary-800 p-4">
        <p className="text-xs leading-relaxed text-foreground-600">
          筛选支持多选组合。同一组内为「或」，不同组之间为「且」，选择后列表会即时刷新。
        </p>
      </div>
    </div>
  );
}